import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import apiRequest, { ApiRequestError } from "hooks/apiRequest";
import styles from "./Quiz.module.scss";
import QuizSkeleton from "./QuizSkeleton/QuizSkeleton";
import QuestionEditor from "./QuestionEditor";
import {
  BackendQuestion,
  EditableQuestion,
  QuestionType,
  QuizInfo,
  TYPE_LABELS,
} from "./types";

type Filter = "ALL" | QuestionType;

const RAMADAN_DAYS = 30;

const emptyQuestion = (date: string): EditableQuestion => ({
  question: "",
  date,
  type: "MULTIPLE_CHOICE",
  category: "",
  hints: [],
  fact: "",
  options: [{ answer: "" }, { answer: "" }, { answer: "" }, { answer: "" }],
  correctIndex: null,
});

const toEditable = (q: BackendQuestion): EditableQuestion => ({
  id: q.id,
  question: q.question,
  date: q.date,
  type: q.type ?? "MULTIPLE_CHOICE",
  category: q.category ?? "",
  hints: q.hints ?? [],
  fact: q.fact ?? "",
  options: q.options.map((o) => ({ id: o.id, answer: o.answer })),
  correctIndex: q.options.some((o) => o.correct) ? q.options.findIndex((o) => o.correct) : null,
});

const toPayload = (q: EditableQuestion, quizId: number) => {
  const hints = q.type === "WHO_AM_I" ? q.hints.map((h) => h.trim()).filter(Boolean) : undefined;
  const base = {
    question: q.question.trim(),
    date: q.date,
    type: q.type,
    category: q.category || undefined,
    fact: q.fact.trim() || undefined,
    hints,
    quizId,
  };
  if (q.id === undefined) {
    return {
      ...base,
      options: q.options.map((o, i) => ({ answer: o.answer.trim(), correct: i === q.correctIndex })),
    };
  }
  return {
    ...base,
    hints: hints ?? [],
    options: q.options.map((o, i) => ({
      id: o.id,
      answer: o.answer.trim(),
      correct: i === q.correctIndex,
      questionId: q.id,
    })),
  };
};

const formatDate = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString("nb-NO", { day: "numeric", month: "short" });

const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });

const releaseStatus = (date: string) => {
  const today = todayIso();
  if (date < today) return { label: "Publisert", className: styles.statusLive };
  if (date === today) return { label: "I dag 17:00", className: styles.statusToday };
  return { label: "Planlagt", className: styles.statusPlanned };
};

const Quiz: React.FC = () => {
  const [quizzes, setQuizzes] = useState<QuizInfo[]>([]);
  const [selectedQuizId, setSelectedQuizId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<BackendQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("ALL");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<EditableQuestion | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const selectedQuiz = quizzes.find((q) => q.quizId === selectedQuizId) ?? null;

  const loadQuizzes = useCallback(async () => {
    const result = await apiRequest<QuizInfo[]>("/api/v1/quiz/all", "GET");
    setQuizzes(result);
    setSelectedQuizId((current) => current ?? result[0]?.quizId ?? null);
    return result;
  }, []);

  const loadQuestions = useCallback(async (quizId: number) => {
    const result = await apiRequest<BackendQuestion[]>("/api/v1/quiz/questions", "GET", undefined, { quizId });
    setQuestions(result);
  }, []);

  useEffect(() => {
    loadQuizzes()
      .catch((e) => setLoadError(e.message))
      .finally(() => setIsLoading(false));
  }, [loadQuizzes]);

  useEffect(() => {
    if (selectedQuizId === null) return;
    setIsLoading(true);
    loadQuestions(selectedQuizId)
      .then(() => setLoadError(null))
      .catch((e) => setLoadError(e.message))
      .finally(() => setIsLoading(false));
  }, [selectedQuizId, loadQuestions]);

  const visibleQuestions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return [...questions]
      .sort((a, b) => a.date.localeCompare(b.date))
      .filter((q) => filter === "ALL" || (q.type ?? "MULTIPLE_CHOICE") === filter)
      .filter(
        (q) =>
          !term ||
          q.question.toLowerCase().includes(term) ||
          (q.hints ?? []).some((h) => h.toLowerCase().includes(term)) ||
          q.options.some((o) => o.answer.toLowerCase().includes(term)),
      );
  }, [questions, filter, search]);

  const stats = useMemo(() => {
    const today = todayIso();
    const whoAmI = questions.filter((q) => q.type === "WHO_AM_I").length;
    return {
      total: questions.length,
      whoAmI,
      multipleChoice: questions.length - whoAmI,
      published: questions.filter((q) => q.date < today).length,
      missing: Math.max(0, RAMADAN_DAYS - questions.length),
    };
  }, [questions]);

  const nextFreeDate = () => {
    const last = [...questions].sort((a, b) => b.date.localeCompare(a.date))[0];
    if (!last) return "";
    const d = new Date(`${last.date}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  const handleCreateQuiz = async () => {
    const year = new Date().getFullYear() + (quizzes.some((q) => q.year === new Date().getFullYear()) ? 1 : 0);
    try {
      const created = await apiRequest<QuizInfo>("/api/v1/quiz", "POST", {
        name: `Ramadan Quiz ${year}`,
        quizYear: year,
        status: "INACTIVE",
      });
      await loadQuizzes();
      setSelectedQuizId(created.quizId);
      setToast(`Quiz for ${year} er opprettet. Den er inaktiv til du slår den på.`);
    } catch (e) {
      setToast((e as ApiRequestError).message);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedQuiz) return;
    const status = selectedQuiz.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await apiRequest(`/api/v1/quiz/${selectedQuiz.quizId}`, "PATCH", { status });
      await loadQuizzes();
      setToast(status === "ACTIVE" ? "Quizen er aktiv." : "Quizen er satt på pause.");
    } catch (e) {
      setToast((e as ApiRequestError).message);
    }
  };

  const handleSave = async (q: EditableQuestion) => {
    if (!selectedQuiz) return;
    setIsSaving(true);
    try {
      const payload = toPayload(q, selectedQuiz.quizId);
      if (q.id === undefined) {
        await apiRequest("/api/v1/quiz/questions", "POST", payload);
      } else {
        await apiRequest(`/api/v1/quiz/questions/${q.id}`, "PATCH", payload);
      }
      await loadQuestions(selectedQuiz.quizId);
      setEditing(null);
      setToast("Spørsmålet er lagret.");
    } catch (e) {
      setToast(`Kunne ikke lagre: ${(e as ApiRequestError).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!selectedQuiz || !window.confirm("Slette spørsmålet? Dette kan ikke angres.")) return;
    setIsSaving(true);
    try {
      await apiRequest(`/api/v1/quiz/questions/${id}`, "DELETE");
      await loadQuestions(selectedQuiz.quizId);
      setEditing(null);
      setToast("Spørsmålet er slettet.");
    } catch (e) {
      setToast(`Kunne ikke slette: ${(e as ApiRequestError).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const toastView = (
    <ToastContainer position="top-end" className="p-3">
      <Toast onClose={() => setToast(null)} show={!!toast} delay={4000} autohide>
        <Toast.Body>{toast}</Toast.Body>
      </Toast>
    </ToastContainer>
  );

  if (editing) {
    return (
      <div className={styles.page}>
        <button type="button" className={styles.backLink} onClick={() => setEditing(null)}>
          ← Tilbake til quiz
        </button>
        <h1 className={styles.title}>{editing.id ? "Rediger spørsmål" : "Nytt spørsmål"}</h1>
        <QuestionEditor
          key={editing.id ?? "new"}
          initial={editing}
          isSaving={isSaving}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          onDelete={editing.id !== undefined ? () => handleDelete(editing.id!) : undefined}
        />
        {toastView}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Ramadan {selectedQuiz?.year ?? ""}</p>
          <h1 className={styles.title}>{selectedQuiz?.name ?? "Quiz"}</h1>
        </div>
        <div className={styles.headerActions}>
          {quizzes.length > 1 && (
            <select
              aria-label="Velg quiz"
              className={styles.select}
              value={selectedQuizId ?? ""}
              onChange={(e) => setSelectedQuizId(Number(e.target.value))}
            >
              {quizzes.map((q) => (
                <option key={q.quizId} value={q.quizId}>
                  {q.year} – {q.name}
                </option>
              ))}
            </select>
          )}
          {selectedQuiz && (
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={selectedQuiz.status === "ACTIVE"}
                onChange={handleToggleActive}
              />
              Quiz aktiv
            </label>
          )}
          <button type="button" className={styles.secondaryButton} onClick={handleCreateQuiz}>
            Ny quiz
          </button>
          {selectedQuiz && (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setEditing(emptyQuestion(nextFreeDate()))}
            >
              + Nytt spørsmål
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <p role="alert" className={styles.errorText}>
          Kunne ikke hente quiz: {loadError}
        </p>
      )}

      {isLoading ? (
        <QuizSkeleton />
      ) : !selectedQuiz ? (
        <div className={styles.panel}>
          <p>Det finnes ingen quiz ennå. Opprett en for å legge inn spørsmål.</p>
        </div>
      ) : (
        <>
          <div className={styles.statGrid}>
            <div className={styles.stat}>
              <span>Spørsmål</span>
              <strong>{stats.total}</strong>
              <small>
                {stats.multipleChoice} flervalg · {stats.whoAmI} Hvem er jeg?
              </small>
            </div>
            <div className={styles.stat}>
              <span>Publisert</span>
              <strong>{stats.published}</strong>
              <small>Nye spørsmål åpner kl. 17:00</small>
            </div>
            <div className={styles.stat}>
              <span>Mangler</span>
              <strong className={stats.missing > 0 ? styles.warningText : ""}>
                {stats.missing} {stats.missing === 1 ? "dag" : "dager"}
              </strong>
              <small>av {RAMADAN_DAYS} dager i Ramadan</small>
            </div>
          </div>

          <section className={styles.tablePanel}>
            <div className={styles.toolbar}>
              <div className={styles.segmented} role="group" aria-label="Filtrer på type">
                {(["ALL", "MULTIPLE_CHOICE", "WHO_AM_I"] as Filter[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    aria-pressed={filter === f}
                    className={filter === f ? styles.segmentActive : ""}
                    onClick={() => setFilter(f)}
                  >
                    {f === "ALL" ? "Alle" : TYPE_LABELS[f]}
                  </button>
                ))}
              </div>
              <input
                type="search"
                className={styles.search}
                placeholder="Søk i spørsmål"
                aria-label="Søk i spørsmål"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Dato</th>
                    <th>Type</th>
                    <th>Spørsmål</th>
                    <th>Riktig svar</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQuestions.map((q) => {
                    const status = releaseStatus(q.date);
                    const isWhoAmI = q.type === "WHO_AM_I";
                    return (
                      <tr key={q.id}>
                        <td className={styles.strong}>{q.questionNumber}</td>
                        <td className={styles.muted}>{formatDate(q.date)}</td>
                        <td>
                          <span className={`${styles.pill} ${isWhoAmI ? styles.pillWhoAmI : styles.pillChoice}`}>
                            {TYPE_LABELS[q.type ?? "MULTIPLE_CHOICE"]}
                          </span>
                        </td>
                        <td>
                          <button type="button" className={styles.rowLink} onClick={() => setEditing(toEditable(q))}>
                            {isWhoAmI
                              ? `${q.category || "Hvem er jeg?"} · ${q.hints?.length ?? 0} hint`
                              : q.question}
                          </button>
                        </td>
                        <td className={styles.muted}>{q.options.find((o) => o.correct)?.answer}</td>
                        <td>
                          <span className={`${styles.pill} ${status.className}`}>{status.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleQuestions.length === 0 && (
                    <tr>
                      <td colSpan={6} className={styles.empty}>
                        Ingen spørsmål her ennå.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
      {toastView}
    </div>
  );
};

export default Quiz;
